var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import { match } from 'ts-pattern';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useAppComponents from '@/hooks/useAppComponents';
import SignupBoxInput from '@/screens/SignupScreen/components/SignupBoxInput';
import ToastUtils from '@/utils/ToastUtils';
const TIMER_DURATION = 180; // 3분
export default function UserPhoneForm({ phoneNumber, onPhoneNumberChange, onVerificationComplete, onVerifyReset, onCodeInputStepChange, onVerifyActiveChange, onVerifyRequest, accessToken, }) {
    const { api } = useAppComponents();
    const [step, setStep] = useState('INPUT_PHONE');
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('IDLE');
    const [timeRemaining, setTimeRemaining] = useState(TIMER_DURATION);
    const [token, setToken] = useState('');
    const [isRequesting, setIsRequesting] = useState(false);
    const phoneInputRef = useRef(null);
    const codeInputRef = useRef(null);
    const timerRef = useRef(null);
    // 타이머 정리
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);
    // 타이머 포맷 (m:ss)
    const formatTime = useCallback((seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }, []);
    // 타이머 시작
    const startTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setTimeRemaining(TIMER_DURATION);
        timerRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);
    // 타이머 만료 시 정리
    useEffect(() => {
        if (timeRemaining === 0 && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, [timeRemaining]);
    // 전화번호 입력 (숫자만). 인증번호 요청/완료 후 번호를 수정하면 인증을 처음부터 초기화한다.
    const handlePhoneNumberChange = useCallback((text) => {
        const numbersOnly = text.replace(/[^0-9]/g, '');
        onPhoneNumberChange(numbersOnly);
        if (step === 'INPUT_CODE') {
            setStep('INPUT_PHONE');
            setVerificationCode('');
            setVerificationStatus('IDLE');
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            // 이미 인증 완료된 번호를 수정한 경우 → 부모의 인증 완료 플래그도 해제
            onVerifyReset === null || onVerifyReset === void 0 ? void 0 : onVerifyReset();
        }
    }, [onPhoneNumberChange, step, onVerifyReset]);
    // 인증번호 입력 (숫자만)
    const handleCodeChange = useCallback((text) => {
        const numbersOnly = text.replace(/[^0-9]/g, '');
        setVerificationCode(numbersOnly);
        setVerificationStatus('IDLE');
    }, []);
    // 인증번호 요청
    const handleRequestCode = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isRequesting) {
            return;
        }
        setIsRequesting(true);
        try {
            const response = yield api.sendPhoneNumberVerifCodeSmsPost({ phoneNumber }, {
                headers: Object.assign({}, (accessToken && { Authorization: `Bearer ${accessToken}` })),
            });
            setToken(response.data.token);
            setStep('INPUT_CODE');
            setVerificationCode('');
            setVerificationStatus('IDLE');
            startTimer();
            // 인증번호 입력 필드로 포커스 이동
            setTimeout(() => {
                var _a;
                (_a = codeInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
        catch (_a) {
            ToastUtils.show('인증번호 발송에 실패했습니다.');
        }
        finally {
            setIsRequesting(false);
        }
    }), [api, phoneNumber, startTimer, isRequesting, accessToken]);
    // 인증번호 확인
    const handleVerifyCode = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setVerificationStatus('VERIFYING');
        try {
            yield api.updatePhoneNumberWithVerifCodePost({
                phoneNumber,
                verifCode: verificationCode,
                token,
            }, {
                headers: Object.assign({}, (accessToken && { Authorization: `Bearer ${accessToken}` })),
            });
            setVerificationStatus('SUCCESS');
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            onVerificationComplete();
        }
        catch (_a) {
            setVerificationStatus('ERROR');
        }
    }), [
        api,
        phoneNumber,
        verificationCode,
        token,
        onVerificationComplete,
        accessToken,
    ]);
    const isPhoneValid = phoneNumber.length >= 10;
    const isCodeValid = verificationCode.length >= 4;
    const isTimerExpired = timeRemaining === 0;
    // step 변경 알림
    useEffect(() => {
        onCodeInputStepChange === null || onCodeInputStepChange === void 0 ? void 0 : onCodeInputStepChange(step === 'INPUT_CODE');
    }, [step, onCodeInputStepChange]);
    // 전화번호 입력 상태 (인증번호 전송 후 → VALID로 표시)
    const phoneInputState = step === 'INPUT_CODE' ? 'VALID' : undefined;
    // 인증번호 입력 상태
    const codeInputState = match(verificationStatus)
        .with('ERROR', () => ({ errorMessage: '인증번호가 잘못되었습니다.' }))
        .with('SUCCESS', () => 'VALID')
        .otherwise(() => undefined);
    // 인증번호 캡션
    const getCodeCaption = () => {
        if (verificationStatus === 'ERROR') {
            return '인증번호가 잘못되었습니다.';
        }
        if (verificationStatus === 'SUCCESS') {
            return '인증이 완료되었습니다';
        }
        if (isTimerExpired && verificationStatus === 'IDLE') {
            return '인증 시간이 만료되었습니다. 다시 요청해주세요.';
        }
        return undefined;
    };
    const getCodeCaptionColor = () => {
        if (verificationStatus === 'ERROR')
            return color.red;
        if (verificationStatus === 'SUCCESS')
            return color.brand40;
        return color.gray50v2;
    };
    const isRequestButtonActive = isPhoneValid && !isRequesting;
    const isVerifyButtonActive = isCodeValid &&
        !isTimerExpired &&
        verificationStatus !== 'VERIFYING' &&
        verificationStatus !== 'SUCCESS';
    // 인증 버튼 활성 상태 알림
    useEffect(() => {
        onVerifyActiveChange === null || onVerifyActiveChange === void 0 ? void 0 : onVerifyActiveChange(isVerifyButtonActive);
    }, [isVerifyButtonActive, onVerifyActiveChange]);
    // 인증번호 확인 핸들러 등록
    useEffect(() => {
        onVerifyRequest === null || onVerifyRequest === void 0 ? void 0 : onVerifyRequest(handleVerifyCode);
    }, [handleVerifyCode, onVerifyRequest]);
    return (_jsxs(Container, { children: [_jsxs(PhoneSection, { children: [_jsxs(PhoneLabelRow, { children: [_jsx(FieldLabelText, { children: "\uD734\uB300\uC804\uD654\uBC88\uD638" }), _jsx(RequiredMark, { children: " *" })] }), _jsx(SignupBoxInput, { ref: phoneInputRef, value: phoneNumber, onChangeText: handlePhoneNumberChange, placeholder: "'-' \uC5C6\uC774 \uC785\uB825\uD574\uC8FC\uC138\uC694", keyboardType: "number-pad", state: phoneInputState }), step === 'INPUT_CODE' && (_jsx(ResendButtonRow, { children: _jsx(SccTouchableOpacity, { elementName: "phone_verification_resend", onPress: handleRequestCode, disabled: isRequesting, children: _jsx(ResendButtonText, { children: "\uC778\uC99D\uBC88\uD638 \uB2E4\uC2DC \uBC1B\uAE30" }) }) }))] }), step === 'INPUT_CODE' && (_jsx(CodeSection, { children: _jsx(SignupBoxInput, { ref: codeInputRef, value: verificationCode, onChangeText: handleCodeChange, placeholder: "\uC778\uC99D\uBC88\uD638\uB97C \uC785\uB825\uD574\uC8FC\uC138\uC694", keyboardType: "number-pad", editable: verificationStatus !== 'SUCCESS' &&
                        verificationStatus !== 'VERIFYING', state: codeInputState, timer: verificationStatus === 'SUCCESS'
                        ? undefined
                        : formatTime(timeRemaining), caption: getCodeCaption(), captionColor: getCodeCaptionColor() }) })), step === 'INPUT_PHONE' && (_jsx(ActionButton, { elementName: "phone_verification_request", onPress: handleRequestCode, disabled: !isRequestButtonActive, isActive: isRequestButtonActive, children: _jsx(ActionButtonText, { isActive: isRequestButtonActive, children: isRequesting ? '발송중...' : '인증번호 받기' }) }))] }));
}
const Container = styled.View `
  padding-horizontal: 20px;
  gap: 12px;
`;
const PhoneSection = styled.View `
  gap: 8px;
`;
const PhoneLabelRow = styled.View `
  flex-direction: row;
  align-items: center;
  padding-left: 6px;
`;
const FieldLabelText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray80v2};
`;
const RequiredMark = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 18px;
  color: ${color.red};
`;
const ResendButtonRow = styled.View `
  align-items: flex-end;
`;
const ResendButtonText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray50v2};
  text-decoration-line: underline;
`;
const CodeSection = styled.View `
  gap: 12px;
`;
const ActionButton = styled(SccTouchableOpacity) `
  height: 48px;
  width: 100%;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background-color: ${props => props.isActive ? color.brand40 : color.gray15v2};
`;
const ActionButtonText = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${props => (props.isActive ? color.white : color.gray30v2)};
`;
